import React, { useCallback, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import CampaignIcon from '@mui/icons-material/Campaign';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';

import LuxPlusPayloadBuilderSetupGuide from '../PayloadBuilder/LuxPlus/LuxPlusPayloadBuilderSetupGuide';
import LuxPlusPayloadBuilderToolbar, {
  type LuxPlusPayloadBuilderMode,
} from '../PayloadBuilder/LuxPlus/LuxPlusPayloadBuilderToolbar';
import PromoBannerCarouselPayloadBuilderPage from '../PayloadBuilder/LuxPlus/PromoBannerCarousel/PromoBannerCarouselPayloadBuilderPage';
import PromoCardCarouselPayloadBuilderPage from '../PayloadBuilder/LuxPlus/PromoCardCarousel/PromoCardCarouselPayloadBuilderPage';
import { PAYLOAD_BUILDER_ALERT_BUTTON_SX } from '../PayloadBuilder/PayloadBuilderConstants';
import {
  PayloadBuilderToolbarActionsProvider,
  usePayloadBuilderToolbarActionsState,
} from '../PayloadBuilder/PayloadBuilderToolbarActions';

const MODE_LABELS: Record<LuxPlusPayloadBuilderMode, string> = {
  promoCardCarousel: 'Promo Cards',
  promoBannerCarousel: 'Promo Banners',
};

const ALERT_COPY: Record<LuxPlusPayloadBuilderMode, { title: string; description: string }> = {
  promoCardCarousel: {
    title: 'Promo cards payload builder',
    description:
      'Build JSON configs for the LuxPlus+ promo cards shown on the member account page. Use the live preview to copy or download the payload, then paste it into loyalty:luxplusPromoCardCarousel.',
  },
  promoBannerCarousel: {
    title: 'Promo banners payload builder',
    description:
      'Build JSON configs for the LuxPlus+ promo banners shown on the landing and account pages. Use the live preview to copy or download the payload, then paste it into loyalty:luxplusPromoBannerCarousel.',
  },
};

export default function LuxPlusPayloadBuilderPage() {
  const { toolbarActions, setToolbarActions } = usePayloadBuilderToolbarActionsState();

  const [selectedMode, setSelectedMode] = useState<LuxPlusPayloadBuilderMode>('promoCardCarousel');
  const [pendingMode, setPendingMode] = useState<LuxPlusPayloadBuilderMode | null>(null);
  const [isModeConfirmOpen, setIsModeConfirmOpen] = useState(false);
  const [promoCardHasProgress, setPromoCardHasProgress] = useState(false);
  const [promoBannerHasProgress, setPromoBannerHasProgress] = useState(false);

  const applyModeChange = useCallback(
    (mode: LuxPlusPayloadBuilderMode) => {
      setSelectedMode(mode);
      setPromoCardHasProgress(false);
      setPromoBannerHasProgress(false);
      setToolbarActions(null);
    },
    [setToolbarActions],
  );

  const handleRequestModeChange = useCallback(
    (mode: LuxPlusPayloadBuilderMode) => {
      if (mode === selectedMode) return;

      const hasCurrentModeProgress =
        selectedMode === 'promoCardCarousel' ? promoCardHasProgress : promoBannerHasProgress;

      if (hasCurrentModeProgress) {
        setPendingMode(mode);
        setIsModeConfirmOpen(true);
        return;
      }
      applyModeChange(mode);
    },
    [applyModeChange, promoBannerHasProgress, promoCardHasProgress, selectedMode],
  );

  const handleConfirmModeChange = useCallback(() => {
    if (pendingMode) {
      applyModeChange(pendingMode);
    }
    setPendingMode(null);
    setIsModeConfirmOpen(false);
  }, [applyModeChange, pendingMode]);

  const handleCancelModeChange = useCallback(() => {
    setPendingMode(null);
    setIsModeConfirmOpen(false);
  }, []);

  const alertCopy = ALERT_COPY[selectedMode];

  return (
    <PayloadBuilderToolbarActionsProvider onActionsChange={setToolbarActions}>
      <Box>
        <Alert icon={<CampaignIcon />} severity="info" sx={{ mb: 3 }}>
          <AlertTitle>{alertCopy.title}</AlertTitle>
          <Box>{alertCopy.description}</Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1.5 }}
          >
            <LuxPlusPayloadBuilderSetupGuide selectedMode={selectedMode} />
            <Button
              component={Link}
              to="/lab"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              color="info"
              startIcon={<OpenInNewIcon fontSize="small" />}
              sx={PAYLOAD_BUILDER_ALERT_BUTTON_SX}
            >
              Open Lab
            </Button>
          </Stack>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <LuxPlusPayloadBuilderToolbar
            selectedMode={selectedMode}
            standaloneActions={toolbarActions}
            onRequestModeChange={handleRequestModeChange}
          />
        </Box>

        {selectedMode === 'promoCardCarousel' ? (
          <PromoCardCarouselPayloadBuilderPage onProgressChange={setPromoCardHasProgress} />
        ) : null}
        {selectedMode === 'promoBannerCarousel' ? (
          <PromoBannerCarouselPayloadBuilderPage onProgressChange={setPromoBannerHasProgress} />
        ) : null}

        <Dialog open={isModeConfirmOpen} onClose={handleCancelModeChange} maxWidth="xs" fullWidth>
          <DialogTitle>Switch payload type?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Switching from {MODE_LABELS[selectedMode]} to {pendingMode ? MODE_LABELS[pendingMode] : 'another type'}{' '}
              will clear all fields you have entered. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelModeChange}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleConfirmModeChange}>
              Switch payload type
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PayloadBuilderToolbarActionsProvider>
  );
}
